/**
 * Product card quick add, the round cart button in the photo's corner:
 * - Touch screens: the first tap expands the circle into a labelled pill ("Choose shade",
 *   "Add to cart"); the second tap does the action. With a mouse, hover expands it (CSS) and one
 *   click acts, so nothing is intercepted there.
 * - Every pointer: the cart icon wiggles when its card scrolls into view and every few seconds
 *   while it stays in view, as a cue. Skipped when reduced motion is requested (the animation
 *   itself is in quick-add-styles.liquid).
 */

const BUTTON_SELECTOR = '.quick-add__button';
const EXPANDED_ATTRIBUTE = 'data-expanded';
const COLLAPSE_DELAY = 4000;
const WIGGLE_CLASS = 'quick-add__button--wiggle';

const touchOnly = window.matchMedia('(hover: none)');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {HTMLElement | null} */
let expandedButton = null;
/** @type {number | undefined} */
let collapseTimer;

const collapse = () => {
  window.clearTimeout(collapseTimer);
  expandedButton?.removeAttribute(EXPANDED_ATTRIBUTE);
  expandedButton = null;
};

/** @param {HTMLElement} button */
const expand = (button) => {
  collapse();
  button.setAttribute(EXPANDED_ATTRIBUTE, 'true');
  expandedButton = button;
  collapseTimer = window.setTimeout(collapse, COLLAPSE_DELAY);
};

/**
 * Runs in the capture phase on window, before the theme's own document-level click routing,
 * so the first tap can be stopped before it adds to cart or opens the quick add window.
 * @param {MouseEvent} event
 */
const handleClick = (event) => {
  if (!touchOnly.matches) return;

  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(BUTTON_SELECTOR);

  if (!(button instanceof HTMLElement)) {
    collapse();
    return;
  }

  if (button.getAttribute(EXPANDED_ATTRIBUTE) === 'true' || button.matches(':disabled')) {
    window.clearTimeout(collapseTimer);
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  expand(button);
};

window.addEventListener('click', handleClick, { capture: true });

/* The icon wiggles when its card comes into view, then every few seconds while it stays in view.
   Buttons that are expanded, hovered or focused are left alone. */
const WIGGLE_INTERVAL = 5000;
const WIGGLE_STAGGER = 150;

/** @type {Set<Element>} */
const visibleButtons = new Set();
/** @type {number | undefined} */
let wiggleTimer;

/** @param {Element} button */
const wiggle = (button) => {
  if (reduceMotion.matches || document.hidden) return;
  if (button.getAttribute(EXPANDED_ATTRIBUTE) === 'true' || button.matches(':hover, :focus-within')) return;

  button.classList.remove(WIGGLE_CLASS);
  button.addEventListener('animationend', () => button.classList.remove(WIGGLE_CLASS), { once: true });
  // Restart the animation on the next frame so a repeat always plays
  window.requestAnimationFrame(() => button.classList.add(WIGGLE_CLASS));
};

const wiggleVisible = () => {
  let index = 0;
  for (const button of visibleButtons) {
    window.setTimeout(() => wiggle(button), index * WIGGLE_STAGGER);
    index += 1;
  }
};

const updateWiggleTimer = () => {
  if (visibleButtons.size > 0 && wiggleTimer === undefined) {
    wiggleTimer = window.setInterval(wiggleVisible, WIGGLE_INTERVAL);
  } else if (visibleButtons.size === 0 && wiggleTimer !== undefined) {
    window.clearInterval(wiggleTimer);
    wiggleTimer = undefined;
  }
};

const wiggleObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        visibleButtons.add(entry.target);
        wiggle(entry.target);
      } else {
        visibleButtons.delete(entry.target);
      }
    }
    updateWiggleTimer();
  },
  { threshold: 0.75 }
);

/** @type {WeakSet<Element>} */
const observed = new WeakSet();

const observeButtons = () => {
  for (const button of visibleButtons) {
    if (!button.isConnected) visibleButtons.delete(button);
  }
  updateWiggleTimer();

  for (const button of document.querySelectorAll(BUTTON_SELECTOR)) {
    if (observed.has(button)) continue;
    observed.add(button);
    wiggleObserver.observe(button);
  }
};

observeButtons();

/* Cards can arrive later: filtering, infinite scroll, section rendering and recommendations */
let pendingScan = 0;
new MutationObserver(() => {
  if (pendingScan) return;
  pendingScan = window.requestAnimationFrame(() => {
    pendingScan = 0;
    observeButtons();
  });
}).observe(document.body, { childList: true, subtree: true });
